"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { VersionTagRibbon } from "modules/common/components/VersionTagRibbon";
import DiscordIcon from "./Icons/DiscordIcon";
import FacebookIcon from "./Icons/FacebookIcon";
import InstagramIcon from "./Icons/InstagramIcon";
import XIcon from "./Icons/XIcon";
import { WhatsNewLink } from "modules/changelog/components/WhatsNewLink";
import { showCookiePreferences } from "modules/common/components/Providers/TermlyCMPProvider";
import {
  TERMLY_COOKIE_POLICY_URL,
  TERMLY_TERMS_OF_SERVICE_URL,
} from "modules/common/constants/termly";
import { DISCORD_INVITE_URL } from "modules/common/constants/support";
import {
  FACEBOOK_URL,
  INSTAGRAM_URL,
  X_URL,
} from "modules/common/constants/social";

export default function Footer() {
  const pathname = usePathname();
  const isHomepage = pathname === "/";

  return (
    <footer
      className={`md:footer-horizontal footer bg-base-200 mt-auto ${!isHomepage ? "hidden lg:block" : ""}`}
    >
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-5">
          <div className="space-y-4">
            <h3 className="text-lg font-bold">Lootstack</h3>
            <ul className="space-y-2">
              <li>
                <FooterLink href="/about">About</FooterLink>
              </li>
              <li>
                <FooterLink href="/privacy">Privacy Policy</FooterLink>
              </li>
              <li>
                <ExternalFooterLink href={TERMLY_COOKIE_POLICY_URL}>
                  Cookie Policy
                </ExternalFooterLink>
              </li>
              <li>
                <ExternalFooterLink href={TERMLY_TERMS_OF_SERVICE_URL}>
                  Terms of Service
                </ExternalFooterLink>
              </li>
              <li>
                <button
                  type="button"
                  onClick={showCookiePreferences}
                  className="text-base-content/70 hover:text-primary flex items-center text-sm transition-colors duration-200"
                >
                  Cookie Settings
                </button>
              </li>
              <li>
                <span
                  className={
                    "text-base-content/70 hover:text-primary flex items-center text-sm transition-colors duration-200"
                  }
                >
                  <WhatsNewLink />
                </span>
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-bold">Resources</h3>
            <ul className="space-y-2">
              <li>
                <FooterLink href="/help">Help</FooterLink>
              </li>
              <li>
                <FooterLink href="/help/faq">FAQ</FooterLink>
              </li>
              <li>
                <FooterLink href="/contact">Contact Us</FooterLink>
              </li>
              {/* Removed sitemap link */}
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-bold">Tools</h3>
            <ul className="space-y-2">
              <li>
                <FooterLink href="/swap-calculator">Swap Calculator</FooterLink>
              </li>
              <li>
                <FooterLink href="/scanner">Scanner</FooterLink>
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-bold">Socials</h3>
            <div className="flex space-x-4">
              <SocialIcon
                href={DISCORD_INVITE_URL}
                icon={DiscordIcon}
                label="Discord"
              />
              <SocialIcon
                href={INSTAGRAM_URL}
                icon={InstagramIcon}
                label="Instagram"
              />
              <SocialIcon
                href={FACEBOOK_URL}
                icon={FacebookIcon}
                label="Facebook"
              />
              <SocialIcon href={X_URL} icon={XIcon} label="X" />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-bold">Legal</h3>
            <p className="text-base-content/70 text-sm">
              All Game Assets are the intellectual property of their respective
              owners. Lootstack does not claim rights to any Game Assets on the
              Platform.
            </p>
          </div>
        </div>

        <div className="border-base-300 mt-8 flex flex-col items-center justify-between border-t pt-4 md:flex-row">
          <div className="mb-1 flex flex-col items-center md:mb-0 md:items-start">
            <p className="text-base-content/70 text-sm">
              &copy; {new Date().getFullYear()} Lootstack. All rights reserved.
              &nbsp;
              <VersionTagRibbon />
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className="text-base-content/70 hover:text-primary flex items-center text-sm transition-colors duration-200"
    >
      {children}
    </Link>
  );
}

function ExternalFooterLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-base-content/70 hover:text-primary flex items-center text-sm transition-colors duration-200"
    >
      {children}
    </a>
  );
}

function SocialIcon({
  href,
  icon: Icon,
  label,
}: {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="btn btn-circle btn-sm border-base-300 bg-base-100 text-base-content hover:border-primary hover:bg-primary hover:text-primary-content transition-all duration-300"
    >
      <Icon className="h-5 w-5" />
      <span className="sr-only">{label}</span>
    </Link>
  );
}
